import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DiffResponse } from '../../services/caseWorkflowService';
import { BUILTIN_SCOPE_CAPTION, DIFF_HEALTHY, transitionLabel } from './caseWorkflowVocabulary';
import { dsVar, ink } from '../../theme/tokens';

/**
 * Whether the documented workflow is the one actually running.
 *
 * The governance answer, and the first question an auditor asks. An empty diff
 * is the HEALTHY state and reads as a statement, not as an empty list — a blank
 * panel here would look like a load failure rather than a clean bill.
 */
const DiffRow: React.FC<{ diff: DiffResponse }> = ({ diff }) => {
    const clean = !diff.declaredNotObserved.length && !diff.observedNotDeclared.length;

    return (
        <Box className="flex flex-col gap-2">
            <Box className="flex items-start gap-2">
                {clean ? (
                    <CheckCircleOutlineIcon sx={{ fontSize: 18, color: ink('#10B981'), mt: '1px' }} />
                ) : (
                    <ErrorOutlineIcon sx={{ fontSize: 18, color: ink('#F87171'), mt: '1px' }} />
                )}
                <Box>
                    <Typography sx={{ fontSize: '0.8125rem', color: clean ? ink('#A7F3D0') : ink('#FCA5A5') }}>
                        {clean
                            ? DIFF_HEALTHY
                            : 'The declared workflow and what actually ran do not agree.'}
                    </Typography>
                    {!clean && (
                        <Box className="mt-1 flex flex-col gap-0.5">
                            {diff.declaredNotObserved.map((code) => (
                                <Typography key={code} sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                                    {transitionLabel(code)} is declared but never happened in this window.
                                </Typography>
                            ))}
                            {diff.observedNotDeclared.map((code) => (
                                <Typography key={code} sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                                    {transitionLabel(code)} happened but is not declared anywhere.
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Warnings do not gate anything; they are rendered, not enforced. */}
            {diff.warnings.map((warning) => (
                <Box key={`${warning.rule}-${warning.path}`} className="flex items-start gap-2">
                    <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: ink('#FBBF24'), mt: '1px' }} />
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                        {warning.message}
                    </Typography>
                </Box>
            ))}

            {diff.scopeRung === 'builtin' && (
                <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), ml: '26px' }}>
                    {BUILTIN_SCOPE_CAPTION}
                </Typography>
            )}
        </Box>
    );
};

export default DiffRow;
